import React, { useState, useEffect } from 'react';
import { useCart } from '../contexts/CartContext';
import { useFirebase } from '../contexts/FirebaseContext';
import { useAddress } from '../contexts/AddressContext';
import { usePaymentMethods } from '../contexts/PaymentMethodsContext';
import { doc, setDoc, collection, getDoc, onSnapshot, updateDoc, deleteDoc, Timestamp, serverTimestamp, runTransaction } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { computeOrderBreakdown, round2 } from '../utils/commission';

import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, CreditCard, PlusCircle,
  CheckCircle, AlertCircle, Info, Heart, Pencil, CircleDollarSign,
  XCircle, ArrowRight, Plus, MoonStar
} from 'lucide-react';
import { formatCurrencyXCD } from '../config/JadConfig';
import { jadPayments } from '../utils/JadPaymentManager';
import { ListSection, SegmentedControl, Sheet } from '../components/ios';

// iOS navigation bar (inline title + back chevron) for pushed pages.
function IOSNavBar({ title, onBack }) {
  return (
    <header className="sticky top-0 z-40 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
      <div className="relative flex items-center px-3 py-2 max-w-lg mx-auto">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          className="w-9 h-9 rounded-full bg-[#F2F2F7] flex items-center justify-center shrink-0 active:bg-gray-200"
        >
          <ChevronLeft size={20} className="text-gray-700" />
        </button>
        <h1 className="absolute left-1/2 -translate-x-1/2 text-base font-semibold text-gray-900">
          {title}
        </h1>
      </div>
    </header>
  );
}

// Checkout Form Component
// Mirrors KFCStKitts/Views/Customer/CheckoutView.swift — iOS grouped Form with
// sections: Order Type → Customer Information → Payment Method → Tip (delivery
// only) → Delivery/Pickup Information → Order Summary, plus a sticky bottom
// total + Place Order bar.
// Cross-island delivery guard — mirrors iOS CheckoutView.isCrossIslandDelivery.
// Normalizes 'Saint Kitts' / 'St. Kitts' / 'saintKitts' → 'kitts', 'Nevis' → 'nevis'.
const islandKey = (v) => {
  const s = String(v || '').toLowerCase();
  if (s.includes('nevis')) return 'nevis';
  if (s.includes('kitt')) return 'kitts';
  return null;
};
// Same bounding box as iOS Island.determineIsland — fallback when the
// island string is missing or unrecognized.
const islandFromCoords = (coords) => {
  const lat = Number(coords?.latitude);
  const lng = Number(coords?.longitude);
  if (!lat || !lng) return null;
  const nevis = lat > 17.0 && lat < 17.3 && lng > -62.7 && lng < -62.5;
  return nevis ? 'nevis' : 'kitts';
};
const islandOf = (islandStr, coords) => islandKey(islandStr) || islandFromCoords(coords);
const islandLabel = (k) => (k === 'nevis' ? 'Nevis' : 'St. Kitts');

function CheckoutForm() {
  const { functions, user, db } = useFirebase();
  const {
    items,
    orderType,
    getSubtotal,
    getChefServiceFee,
    getDeliveryFee,
    getTotal,
    clearCart,
    deliveryAddress,
    restaurantId,
    restaurantName,
    restaurantLocation,
    setOrderTypeAndLocation,
    updateDeliveryAddress
  } = useCart();
  const { addresses, formatAddress, getDefaultAddress } = useAddress();

  // Cross-island delivery guard: the delivery address must be on the same
  // island as the restaurant — drivers never see orders outside their island.
  const restaurantIslandKey = islandOf(restaurantLocation?.island, restaurantLocation?.coordinates);
  const addressIslandKey = islandOf(deliveryAddress?.island, deliveryAddress?.coordinates);
  const crossIslandDelivery = orderType === 'delivery' && !!restaurantIslandKey &&
    !!addressIslandKey && restaurantIslandKey !== addressIslandKey;
  const { paymentMethods, formatPaymentMethod, getDefaultPaymentMethod, loadPaymentMethods } = usePaymentMethods();
  const navigate = useNavigate();

  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // Drives the "Order Placed!" alert — mirrors iOS showingOrderConfirmation
  const [placedOrder, setPlacedOrder] = useState(null);

  // Payment method selection
  const [paymentMethodType, setPaymentMethodType] = useState('new'); // 'new' or 'saved'
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('');

  // JAD card + billing details (required by JAD for every payment)
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpMonth, setCardExpMonth] = useState('');
  const [cardExpYear, setCardExpYear] = useState('');
  const [saveCard, setSaveCard] = useState(false);
  const [billingFirstName, setBillingFirstName] = useState('');
  const [billingLastName, setBillingLastName] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [billingIsland, setBillingIsland] = useState('St. Kitts');

  // Tip selection state (mirrors iOS TipOption: none / 2 / 5 / 10 / custom)
  const [tipOption, setTipOption] = useState('none');
  const [customTipAmount, setCustomTipAmount] = useState('');
  const [showingCustomTipInput, setShowingCustomTipInput] = useState(false);

  // Address sheet + delivery availability state
  const [showingAddressSheet, setShowingAddressSheet] = useState(false);
  const [isDeliveryEnabled, setIsDeliveryEnabled] = useState(true);
  const [isAppOffline, setIsAppOffline] = useState(false);

  const tipAmount = tipOption === 'custom'
    ? (parseFloat(customTipAmount) || 0)
    : (tipOption === 'none' ? 0 : parseFloat(tipOption));

  // Calculate amounts
  const subtotal = getSubtotal();
  const chefServiceFee = getChefServiceFee();
  const deliveryFee = getDeliveryFee();
  const total = getTotal();
  const finalTotal = total + tipAmount;

  // Load user profile data
  const loadUserProfile = async () => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();

        if (userData.firstName && userData.lastName) {
          setCustomerName(`${userData.firstName} ${userData.lastName}`);
          setBillingFirstName(userData.firstName);
          setBillingLastName(userData.lastName);
        } else if (userData.fullName) {
          setCustomerName(userData.fullName);
          const parts = userData.fullName.trim().split(/\s+/);
          setBillingFirstName(parts[0] || '');
          setBillingLastName(parts.slice(1).join(' ') || parts[0] || '');
        }
        // Prefill billing saved from the customer's last paid order
        if (userData.billing) {
          const b = userData.billing;
          if (b.firstName) setBillingFirstName(b.firstName);
          if (b.lastName) setBillingLastName(b.lastName);
          if (b.address1) setBillingAddress(b.address1);
          if (b.state) setBillingIsland(b.state);
        }
        if (userData.phone) {
          setCustomerPhone(userData.phone);
        } else if (userData.phoneNumber) {
          setCustomerPhone(userData.phoneNumber);
        }
      }
    } catch (error) {
      console.error('❌ Error loading user profile:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUserProfile();
  }, [user, db]);

  // Load saved payment methods (mirrors iOS loadPaymentMethods on appear)
  useEffect(() => {
    if (user?.uid) {
      loadPaymentMethods();
    }
  }, [user?.uid]);

  // Auto-select the default saved card once methods load (mirrors iOS)
  useEffect(() => {
    if (paymentMethods.length > 0 && !selectedPaymentMethodId) {
      const defaultMethod = getDefaultPaymentMethod();
      if (defaultMethod) {
        setSelectedPaymentMethodId(defaultMethod.id);
        setPaymentMethodType('saved');
      }
    }
  }, [paymentMethods, selectedPaymentMethodId]);

  // Monitor delivery availability (mirrors iOS DeliveryStateManager)
  useEffect(() => {
    if (db) {
      const settingsRef = doc(db, 'settings', 'global');
      const unsubscribe = onSnapshot(settingsRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setIsDeliveryEnabled(data.isDeliveryEnabled !== false);
          setIsAppOffline(data.isAppOffline || false);
        }
      });
      return unsubscribe;
    }
  }, [db]);

  // When delivery is disabled, force pickup (mirrors iOS orderTypeSection onAppear)
  useEffect(() => {
    if (!isDeliveryEnabled && orderType === 'delivery') {
      setOrderTypeAndLocation('pickup', restaurantLocation, null);
    }
  }, [isDeliveryEnabled, orderType]);

  // Auto-select default address for delivery when none is selected
  // (mirrors iOS loadAddresses default-address selection)
  useEffect(() => {
    if (orderType === 'delivery' && !deliveryAddress && addresses.length > 0) {
      const defaultAddr = getDefaultAddress();
      if (defaultAddr) {
        updateDeliveryAddress(defaultAddr);
      }
    }
  }, [orderType, deliveryAddress, addresses]);

  // Handle order type change (moved here from cart — mirrors iOS segmented picker)
  const handleOrderTypeChange = (type) => {
    if (type === 'delivery') {
      const defaultAddr = getDefaultAddress();
      setOrderTypeAndLocation(type, restaurantLocation, defaultAddr || null);
    } else {
      setOrderTypeAndLocation(type, restaurantLocation, null);
    }
  };

  // Handle address selection from the sheet (mirrors iOS updateCartWithAddress)
  const handleAddressSelect = (address) => {
    updateDeliveryAddress(address);
    setShowingAddressSheet(false);
  };

  // Tip option selection (mirrors iOS tipOptionButton)
  const handleTipSelect = (option) => {
    setTipOption(option);
    setShowingCustomTipInput(false);
    if (option !== 'custom') {
      setCustomTipAmount('');
    } else {
      setShowingCustomTipInput(true);
    }
  };

  // Handle payment and order creation — order is created first so the backend
  // reads the trusted total from Firestore, then JAD authorizes against it.
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isAppOffline) {
      setError("We're currently offline. Please check back later.");
      return;
    }

    if (!customerName.trim() || !customerPhone.trim()) {
      setError('Please fill in customer information');
      return;
    }

    if (orderType === 'delivery' && !deliveryAddress) {
      setError('Please select a delivery address');
      return;
    }

    if (crossIslandDelivery) {
      setError(`${restaurantName || 'This restaurant'} is on ${islandLabel(restaurantIslandKey)} — delivery requires an address on ${islandLabel(restaurantIslandKey)}. Choose a different address or switch to pickup.`);
      return;
    }

    // Validate payment method selection
    if (paymentMethodType === 'saved' && !selectedPaymentMethodId) {
      setError('Please select a saved payment method');
      return;
    }

    // JAD requires billing details on every payment (name + street + island)
    const missingBilling = [];
    if (!billingFirstName.trim()) missingBilling.push('first name');
    if (!billingLastName.trim()) missingBilling.push('last name');
    if (!billingAddress.trim()) missingBilling.push('billing address');
    if (missingBilling.length) {
      setError(`Missing billing details: ${missingBilling.join(', ')}`);
      return;
    }

    if (paymentMethodType === 'new') {
      const digits = cardNumber.replace(/[\s-]/g, '');
      if (!/^\d{13,19}$/.test(digits)) {
        setError('Enter a valid card number');
        return;
      }
      if (!/^(0[1-9]|1[0-2])$/.test(cardExpMonth)) {
        setError('Enter a valid expiry month (MM)');
        return;
      }
      if (!/^20[2-9]\d$/.test(cardExpYear)) {
        setError('Enter a valid expiry year (YYYY)');
        return;
      }
    }

    setProcessing(true);
    setError(null);

    try {
      // Stock check BEFORE charging the card — items that track stock must
      // have enough on hand for the requested quantity.
      for (const item of items) {
        try {
          // Time-boxed so a flaky connection can never leave checkout spinning.
          const itemSnap = await Promise.race([
            getDoc(doc(db, 'restaurants', restaurantId, 'menuItems', item.id)),
            new Promise((_, reject) => setTimeout(() => reject(new Error('stock check timed out')), 8000)),
          ]);
          const d = itemSnap.data() || {};
          const sized = item.variantName && Array.isArray(d.variants)
            ? d.variants.find((v) => v && v.name === item.variantName)
            : null;
          if (sized && Number.isFinite(sized.stock)) {
            // Sized items are checked against the chosen size's own stock.
            if (sized.stock < (item.quantity || 1)) {
              setError(
                sized.stock <= 0
                  ? `"${item.name}" in size ${item.variantName} is out of stock.`
                  : `Only ${sized.stock} left of "${item.name}" in size ${item.variantName}.`
              );
              setProcessing(false);
              return;
            }
          } else if (d.trackStock === true) {
            const left = Number.isFinite(d.stockCount) ? d.stockCount : 0;
            if (left < (item.quantity || 1)) {
              setError(
                left <= 0
                  ? `"${item.name}" is out of stock.`
                  : `Only ${left} left of "${item.name}".`
              );
              setProcessing(false);
              return;
            }
          }
        } catch (e) {
          console.warn('stock check failed for', item.id, e);
        }
      }

      // Commission breakdown — mirrors iOS CommissionCalculator.computeBreakdown.
      // The order doc stores every payout field at creation like iOS does.
      const isDelivery = orderType === 'delivery';
      const breakdown = computeOrderBreakdown({ subtotal, deliveryFee, orderType });
      // Firestore auto-ID so web orders look like iOS orders (letters +
      // numbers, not order_<timestamp> which truncates to "order_17" in the
      // restaurant UI). orderNumber mirrors iOS "ORD-<8 UUID chars>".
      const orderRef = doc(collection(db, 'orders'));
      const orderId = orderRef.id;
      const uuid = crypto.randomUUID?.() || `${Date.now().toString(16)}${Math.random().toString(16).slice(2)}`;
      const orderNumber = `ORD-${uuid.replace(/-/g, '').slice(0, 8).toUpperCase()}`;

      // Create the order as awaiting_payment — hidden from the restaurant until
      // the JAD authorization succeeds and the backend flips it to pending.
      const orderData = {
        id: orderId,
        orderNumber,
        customerId: user.uid,
        customerName: customerName,
        restaurantId: restaurantId,
        restaurantName: restaurantName,
        // Real iOS Location shape stored by CartContext.setRestaurant —
        // OrderTrackingPage reads restaurantLocation.coordinates.
        restaurantLocation: restaurantLocation || {
          name: restaurantName,
          address: '',
          coordinates: { latitude: 0, longitude: 0 },
          parish: '',
          island: ''
        },
        orderItems: items.map(item => ({
          menuItem: {
            id: item.id,
            name: item.name,
            price: item.price || 0,
            restaurantId: restaurantId,
            menuItemImageURL: item.menuItemImageURL || item.imageURL || '',
            category: item.category || '',
            description: item.description || ''
          },
          quantity: item.quantity,
          totalPrice: (item.price || 0) * item.quantity,
          id: item.id,
          menuItemId: item.id,
          menuItemName: item.name,
          variantName: item.variantName || null,
          price: item.price || 0
        })),
        orderType: isDelivery ? 'Delivery' : 'Pickup',
        status: 'awaiting_payment',
        // iOS Order: totalAmount = breakdown.totalAmount + tip (charged in XCD)
        totalAmount: round2(finalTotal),
        subtotal: subtotal,
        chefServiceFee: chefServiceFee,
        chefServiceFeeAmount: chefServiceFee,
        deliveryFee: isDelivery ? deliveryFee : 0,
        tipAmount: tipAmount, // mirrors iOS placeOrder(tipAmount:)
        restaurantPayout: breakdown.restaurantPayout,
        driverPayout: breakdown.driverPayout,
        driverCommission: breakdown.driverCommission,
        platformCommission: breakdown.platformCommission,
        paymentMethod: 'credit card',
        paymentProvider: 'jad',
        paymentStatus: 'awaiting_payment',
        specialInstructions: specialInstructions || '',
        customerPhone: customerPhone,
        customerAddress: isDelivery ? formatAddress(deliveryAddress) : restaurantName,
        deliveryAddress: isDelivery ? deliveryAddress : null,
        estimatedDeliveryTime: Timestamp.fromDate(new Date(Date.now() + 30 * 60 * 1000)),
        pickupTime: orderType === 'pickup' ? (pickupTime || null) : null,
        createdAt: new Date(),
        updatedAt: new Date(),
        metadata: {
          xcd_amount: finalTotal.toString(),
          platform: 'web'
        }
      };

      await setDoc(orderRef, orderData);

      // Authorize the payment with JAD — backend reads totalAmount from the
      // order doc, so the client never dictates the charge amount.
      const billing = {
        firstName: billingFirstName.trim(),
        lastName: billingLastName.trim(),
        address1: billingAddress.trim(),
        state: billingIsland,
        phoneNumber: customerPhone.trim()
      };

      const authResult = await jadPayments(functions).authorize({
        orderId,
        rail: 'card',
        ...(paymentMethodType === 'saved'
          ? { savedCardId: selectedPaymentMethodId }
          : {
              card: {
                number: cardNumber.replace(/[\s-]/g, ''),
                expMonth: cardExpMonth,
                expYear: cardExpYear
              },
              tokenize: saveCard
            }),
        billing
      });

      // Authorization succeeded — order is now pending for the restaurant.
      {
        // Deduct stock — atomic per-item transaction so simultaneous orders
        // can't double-deduct. Clamps at 0; non-blocking on failure.
        for (const item of items) {
          const qty = item.quantity || 1;
          const itemRef = doc(db, 'restaurants', restaurantId, 'menuItems', item.id);
          runTransaction(db, async (tx) => {
            const snap = await tx.get(itemRef);
            const d = snap.data() || {};
            const left = Number.isFinite(d.stockCount) ? d.stockCount : 0;
            let variants = Array.isArray(d.variants) ? d.variants : [];
            const vi = item.variantName ? variants.findIndex((v) => v && v.name === item.variantName) : -1;
            if (vi >= 0 && Number.isFinite(variants[vi].stock)) {
              // Deduct from the purchased size; total stock = sum of sizes.
              variants = variants.map((v, i) => (i === vi ? { ...v, stock: Math.max(0, v.stock - qty) } : v));
              const upd = { variants, updatedAt: serverTimestamp() };
              if (d.trackStock === true) {
                upd.stockCount = variants.every((v) => Number.isFinite(v.stock))
                  ? variants.reduce((s, v) => s + v.stock, 0)
                  : Math.max(0, left - qty);
              }
              tx.update(itemRef, upd);
            } else if (d.trackStock === true) {
              tx.update(itemRef, {
                stockCount: Math.max(0, left - qty),
                updatedAt: serverTimestamp(),
              });
            }
          }).catch(e => console.warn('stock deduction failed for', item.id, e));
        }

        // Stats and the restaurant-owner notification are handled server-side:
        // stats by jadCapture once money actually moves, and the new-order push
        // by onOrderStatusChange when authorization flips the order to Pending.

        // Show the "Order Placed!" alert — cart clears + navigation happens
        // when the customer taps OK (mirrors iOS orderConfirmationActions).
        setPlacedOrder({
          orderId,
          authorizationId: authResult?.authorizationId || null,
          tip: tipAmount
        });
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      setError(error.message || 'Payment processing failed');
    }

    setProcessing(false);
  };

  // Mirrors iOS orderConfirmationActions — OK dismisses the alert, clears the
  // cart, and takes the customer to the order details page.
  const handleOrderConfirmed = () => {
    if (!placedOrder) return;
    const { orderId } = placedOrder;
    setPlacedOrder(null);
    clearCart();
    navigate(`/orders/${orderId}`);
  };

  // Redirect if no items in cart
  if (items.length === 0) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F2F7] flex items-center justify-center px-4">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <h2 className="text-[17px] font-semibold text-gray-900 mb-1">Loading Checkout</h2>
          <p className="text-[15px] text-[#6D6D72]">Getting your information ready...</p>
        </div>
      </div>
    );
  }

  const isFormValid = customerName.trim() && customerPhone.trim() &&
    (orderType !== 'delivery' || (deliveryAddress && !crossIslandDelivery));

  const tipOptions = [
    { key: 'none', label: 'No tip', Icon: XCircle },
    { key: '2', label: formatCurrencyXCD(2), Icon: CircleDollarSign },
    { key: '5', label: formatCurrencyXCD(5), Icon: CircleDollarSign },
    { key: '10', label: formatCurrencyXCD(10), Icon: CircleDollarSign },
  ];

  return (
    <div className="min-h-screen bg-[#F2F2F7]">
      <IOSNavBar title="Checkout" onBack={() => navigate('/dashboard/cart')} />

      <form onSubmit={handleSubmit}>
        <div className="max-w-lg mx-auto pb-36">

          {/* MARK: - App offline notice (iOS red section) */}
          {isAppOffline && (
            <ListSection>
              <div className="px-4 py-3">
                <div className="flex gap-3 p-3 bg-[#FF3B30]/10 rounded-lg">
                  <MoonStar size={20} className="text-[#FF3B30] shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[15px] font-semibold text-[#FF3B30]">We're Currently Offline</p>
                    <p className="text-[13px] text-[#6D6D72] mt-0.5">
                      Please check back later.
                    </p>
                  </div>
                </div>
              </div>
            </ListSection>
          )}

          {/* MARK: - Delivery temporarily unavailable notice (iOS orange info section) */}
          {!isDeliveryEnabled && (
            <ListSection>
              <div className="px-4 py-3">
                <div className="flex gap-3 p-3 bg-[#FF9500]/10 rounded-lg">
                  <Info size={20} className="text-[#FF9500] shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[15px] font-semibold text-[#FF9500]">Delivery Temporarily Unavailable</p>
                    <p className="text-[13px] text-[#6D6D72] mt-0.5">
                      We're only accepting pickup orders at this time. Please visit us at the restaurant to collect your order.
                    </p>
                  </div>
                </div>
              </div>
            </ListSection>
          )}

          {/* MARK: - Order Type (iOS segmented picker) */}
          <ListSection title="Order Type">
            {isDeliveryEnabled ? (
              <div className="py-2">
                <SegmentedControl
                  options={[
                    { label: 'Pickup', value: 'pickup' },
                    { label: 'Delivery', value: 'delivery' }
                  ]}
                  value={orderType}
                  onChange={handleOrderTypeChange}
                />
              </div>
            ) : (
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-[17px] font-semibold text-gray-900">Pickup Only</span>
                <span className="text-[13px] text-[#6D6D72]">Delivery temporarily unavailable</span>
              </div>
            )}
          </ListSection>

          {/* MARK: - Customer Information (iOS TextField rows) */}
          <ListSection title="Customer Information">
            <div className="px-4">
              <input
                type="text"
                placeholder="Full name"
                autoComplete="name"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className="w-full py-3 text-[16px] text-gray-900 placeholder-[#8E8E93] bg-transparent outline-none"
                required
              />
            </div>
            <div className="px-4">
              <input
                type="tel"
                placeholder="Phone"
                autoComplete="tel"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                className="w-full py-3 text-[16px] text-gray-900 placeholder-[#8E8E93] bg-transparent outline-none"
                required
              />
            </div>
          </ListSection>

          {/* MARK: - Payment Method (iOS saved-card rows + add button) */}
          <ListSection title="Payment Method">
            {paymentMethods.length > 0 && (
              <>
                {paymentMethods.map((method) => {
                  const selected = paymentMethodType === 'saved' && selectedPaymentMethodId === method.id;
                  return (
                    <button
                      key={method.id}
                      type="button"
                      onClick={() => {
                        setPaymentMethodType('saved');
                        setSelectedPaymentMethodId(method.id);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left active:bg-gray-100 transition-colors"
                    >
                      <CreditCard size={22} className="text-[#007AFF] shrink-0" />
                      <span className="flex-1 min-w-0">
                        <span className="block text-[15px] text-gray-900 truncate">
                          {formatPaymentMethod(method)}
                          {(method.expMonth || method.expYear) && (
                            <span className="text-[#6D6D72]"> (Exp: {method.expMonth}/{method.expYear})</span>
                          )}
                        </span>
                        {method.isDefault && (
                          <span className="inline-block mt-0.5 text-[11px] text-[#007AFF] bg-[#007AFF]/15 px-1.5 py-0.5 rounded">
                            Default
                          </span>
                        )}
                      </span>
                      {selected && <CheckCircle size={22} className="text-[#007AFF] shrink-0" />}
                    </button>
                  );
                })}
                {/* Pay with a new card row */}
                <button
                  type="button"
                  onClick={() => {
                    setPaymentMethodType('new');
                    setSelectedPaymentMethodId('');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left active:bg-gray-100 transition-colors"
                >
                  <PlusCircle size={22} className="text-[#FFCC00] shrink-0" />
                  <span className="flex-1 text-[15px] font-semibold text-gray-900">New Card</span>
                  {paymentMethodType === 'new' && <CheckCircle size={22} className="text-[#007AFF] shrink-0" />}
                </button>
              </>
            )}

            {/* Card inputs for a new card — JAD takes number + expiry (no CVV) */}
            {(paymentMethodType === 'new' || paymentMethods.length === 0) && (
              <div className="px-4 py-3 space-y-2">
                <p className="text-[13px] text-[#6D6D72] mb-1">Card Details</p>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="Card number"
                  autoComplete="cc-number"
                  value={cardNumber}
                  onChange={(e) => setCardNumber(e.target.value)}
                  className="w-full bg-[#F2F2F7] rounded-lg px-3 py-2.5 text-[16px] text-gray-900 placeholder-[#8E8E93] outline-none"
                />
                <div className="flex gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="MM"
                    autoComplete="cc-exp-month"
                    maxLength={2}
                    value={cardExpMonth}
                    onChange={(e) => setCardExpMonth(e.target.value.replace(/\D/g, ''))}
                    className="w-20 bg-[#F2F2F7] rounded-lg px-3 py-2.5 text-[16px] text-gray-900 placeholder-[#8E8E93] outline-none"
                  />
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="YYYY"
                    autoComplete="cc-exp-year"
                    maxLength={4}
                    value={cardExpYear}
                    onChange={(e) => setCardExpYear(e.target.value.replace(/\D/g, ''))}
                    className="w-24 bg-[#F2F2F7] rounded-lg px-3 py-2.5 text-[16px] text-gray-900 placeholder-[#8E8E93] outline-none"
                  />
                </div>
                <label className="flex items-center gap-2 pt-1 text-[15px] text-gray-900">
                  <input
                    type="checkbox"
                    checked={saveCard}
                    onChange={(e) => setSaveCard(e.target.checked)}
                    className="w-4 h-4 accent-[#007AFF]"
                  />
                  Save this card for next time
                </label>
              </div>
            )}
          </ListSection>

          {/* MARK: - Billing details (required by JAD for every payment) */}
          <ListSection title="Billing Details">
            <div className="px-4 py-3 space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="First name"
                  autoComplete="given-name"
                  value={billingFirstName}
                  onChange={(e) => setBillingFirstName(e.target.value)}
                  className="flex-1 bg-[#F2F2F7] rounded-lg px-3 py-2.5 text-[16px] text-gray-900 placeholder-[#8E8E93] outline-none"
                />
                <input
                  type="text"
                  placeholder="Last name"
                  autoComplete="family-name"
                  value={billingLastName}
                  onChange={(e) => setBillingLastName(e.target.value)}
                  className="flex-1 bg-[#F2F2F7] rounded-lg px-3 py-2.5 text-[16px] text-gray-900 placeholder-[#8E8E93] outline-none"
                />
              </div>
              <input
                type="text"
                placeholder="Billing address"
                autoComplete="street-address"
                value={billingAddress}
                onChange={(e) => setBillingAddress(e.target.value)}
                className="w-full bg-[#F2F2F7] rounded-lg px-3 py-2.5 text-[16px] text-gray-900 placeholder-[#8E8E93] outline-none"
              />
              <select
                value={billingIsland}
                onChange={(e) => setBillingIsland(e.target.value)}
                className="w-full bg-[#F2F2F7] rounded-lg px-3 py-2.5 text-[16px] text-gray-900 outline-none"
              >
                <option value="St. Kitts">St. Kitts</option>
                <option value="Nevis">Nevis</option>
              </select>
            </div>
          </ListSection>

          {/* MARK: - Tip Your Driver (delivery only — mirrors iOS tippingSection) */}
          {orderType === 'delivery' && (
            <ListSection title="Tip Your Driver">
              <div className="px-4 py-3 space-y-3">
                <p className="text-[15px] text-[#6D6D72]">Show appreciation for great service</p>

                {/* Tip option buttons — green outline, selected = red fill */}
                <div className="flex gap-2">
                  {tipOptions.map(({ key, label, Icon }) => {
                    const active = tipOption === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => handleTipSelect(key)}
                        className={`flex-1 flex flex-col items-center gap-0.5 py-2 rounded-lg border-2 transition-colors ${
                          active
                            ? 'bg-[#FF3B30] border-[#FF3B30] text-white'
                            : 'bg-[#34C759]/15 border-[#34C759] text-[#34C759]'
                        }`}
                      >
                        <Icon size={16} />
                        <span className="text-[11px] font-medium">{label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Custom tip input / link */}
                {showingCustomTipInput ? (
                  <div>
                    <p className="text-[15px] text-[#6D6D72] mb-1.5">Enter custom tip amount</p>
                    <div className="flex items-center gap-2">
                      <span className="text-[17px] font-semibold text-[#6D6D72]">$</span>
                      <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={customTipAmount}
                        onChange={(e) => {
                          setCustomTipAmount(e.target.value);
                          setTipOption('custom');
                        }}
                        className="flex-1 bg-white border border-black/10 rounded-lg px-3 py-2 text-[16px] outline-none focus:border-[#FF3B30]"
                      />
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleTipSelect('custom')}
                    className="flex items-center gap-1.5 text-[#007AFF] text-[15px] py-1 active:opacity-70"
                  >
                    <Pencil size={15} />
                    <span>Enter custom amount</span>
                  </button>
                )}

                {/* Selected tip summary */}
                {tipAmount > 0 && (
                  <div className="flex items-center gap-2 bg-[#34C759]/10 rounded-lg px-3 py-2">
                    <Heart size={15} className="text-[#34C759]" />
                    <span className="text-[15px] text-[#34C759] flex-1">
                      Tip: {formatCurrencyXCD(tipAmount)}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setTipOption('none');
                        setCustomTipAmount('');
                        setShowingCustomTipInput(false);
                      }}
                      className="text-[13px] text-[#FF3B30] active:opacity-70"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            </ListSection>
          )}

          {/* MARK: - Delivery / Pickup Information */}
          <ListSection title={orderType === 'delivery' ? 'Delivery Information' : 'Pickup Information'}>
            {orderType === 'delivery' && isDeliveryEnabled ? (
              deliveryAddress ? (
                <div className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => setShowingAddressSheet(true)}
                    className="w-full text-left bg-[#F2F2F7] rounded-lg p-3 active:bg-gray-200 transition-colors"
                  >
                    <p className="text-[15px] text-gray-900">{formatAddress(deliveryAddress)}</p>
                    {deliveryAddress.instructions && (
                      <p className="text-[13px] text-[#6D6D72] mt-0.5">{deliveryAddress.instructions}</p>
                    )}
                    <p className="text-[11px] text-[#007AFF] mt-1">Tap to change address</p>
                  </button>
                  {crossIslandDelivery && (
                    <div className="mt-2 flex items-start gap-2 bg-[#FF3B30]/10 rounded-lg p-3">
                      <span className="text-[#FF3B30] text-[13px] leading-snug">
                        Delivery isn't available across islands. {restaurantName || 'This restaurant'} is on {islandLabel(restaurantIslandKey)} — choose a {islandLabel(restaurantIslandKey)} address or switch to pickup.
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => setShowingAddressSheet(true)}
                    className="w-full bg-[#007AFF] text-white text-[16px] font-medium py-3 rounded-lg active:opacity-80"
                  >
                    Select Delivery Address
                  </button>
                </div>
              )
            ) : (
              <div className="px-4 py-3 space-y-2">
                <p className="text-[15px] text-[#6D6D72]">Pickup Information</p>
                <p className="text-[16px] text-gray-900">You'll pick up your order at the restaurant.</p>
                <div className="pt-2">
                  <label className="block text-[13px] text-[#6D6D72] mb-1.5">Pickup Time</label>
                  <select
                    value={pickupTime}
                    onChange={(e) => setPickupTime(e.target.value)}
                    className="w-full bg-[#F2F2F7] rounded-lg px-3 py-2.5 text-[16px] text-gray-900 outline-none"
                  >
                    <option value="">Select pickup time</option>
                    <option value="asap">ASAP</option>
                    <option value="15min">In 15 minutes</option>
                    <option value="30min">In 30 minutes</option>
                    <option value="1hour">In 1 hour</option>
                  </select>
                </div>
              </div>
            )}

            {/* Special instructions row */}
            <div className="px-4 py-3">
              <textarea
                placeholder="Special instructions (optional)"
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
                rows={2}
                className="w-full bg-[#F2F2F7] rounded-lg px-3 py-2.5 text-[15px] text-gray-900 placeholder-[#8E8E93] outline-none resize-none"
              />
            </div>
          </ListSection>

          {/* MARK: - Order Summary (mirrors iOS OrderSummarySection) */}
          <ListSection title="Order Summary">
            {/* Item rows: "Nx Name ... $X.XX" */}
            {items.map((item) => (
              <div key={item.id} className="flex items-center gap-2 px-4 py-3">
                <span className="text-[15px] text-[#6D6D72]">{item.quantity}x</span>
                <span className="flex-1 text-[15px] text-gray-900 truncate">{item.name}</span>
                <span className="text-[15px] text-gray-900">{formatCurrencyXCD((item.price || 0) * item.quantity)}</span>
              </div>
            ))}

            <div className="flex justify-between px-4 py-3">
              <span className="text-[15px] text-gray-900">Subtotal</span>
              <span className="text-[15px] text-gray-900">{formatCurrencyXCD(subtotal)}</span>
            </div>

            {orderType === 'delivery' && isDeliveryEnabled && (
              <div className="px-4 py-1.5">
                <div className="flex justify-between bg-[#F2F2F7] rounded-md px-2 py-1.5">
                  <span className="text-[15px] font-semibold text-gray-900">Delivery Fee</span>
                  <span className="text-[15px] text-gray-900">{formatCurrencyXCD(deliveryFee)}</span>
                </div>
              </div>
            )}

            <div className="flex justify-between px-4 py-3">
              <span className="text-[15px] text-gray-900">Chef Service Fee (2%)</span>
              <span className="text-[15px] text-gray-900">{formatCurrencyXCD(chefServiceFee)}</span>
            </div>

            {tipAmount > 0 && (
              <div className="flex justify-between px-4 py-3">
                <span className="text-[15px] text-[#34C759]">Driver Tip</span>
                <span className="text-[15px] text-[#34C759]">{formatCurrencyXCD(tipAmount)}</span>
              </div>
            )}

            <div className="flex justify-between px-4 py-3">
              <span className="text-[17px] font-semibold text-[#34C759]">Total</span>
              <span className="text-[17px] font-semibold text-gray-900">{formatCurrencyXCD(finalTotal)}</span>
            </div>
          </ListSection>

          {/* Error display */}
          {error && (
            <div className="px-4 mt-4">
              <div className="bg-[#FF3B30]/10 rounded-xl p-3 flex items-center gap-2">
                <AlertCircle size={18} className="text-[#FF3B30] shrink-0" />
                <p className="text-[15px] font-medium text-[#FF3B30]">{error}</p>
              </div>
            </div>
          )}
        </div>

        {/* MARK: - Sticky bottom bar: total info + Place Order (mirrors iOS) */}
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-black/10 z-40">
          <div className="max-w-lg mx-auto px-4 py-2.5 flex items-center justify-between gap-4 safe-area-bottom">
            <div>
              <p className="text-[17px] font-semibold text-[#34C759]">
                Total: {formatCurrencyXCD(finalTotal)}
              </p>
              <p className="text-[11px] text-[#6D6D72]">
                {orderType === 'delivery' && isDeliveryEnabled ? 'Includes delivery fee' : 'Pickup order'}
              </p>
              {tipAmount > 0 && (
                <p className="text-[11px] text-[#34C759]">
                  Includes tip: {formatCurrencyXCD(tipAmount)}
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={!isFormValid || processing || isAppOffline}
              className={`flex items-center gap-1.5 px-5 py-3 rounded-lg text-[17px] font-semibold text-white transition-colors ${
                isFormValid && !processing && !isAppOffline ? 'bg-[#FF3B30] active:opacity-80' : 'bg-[#8E8E93]'
              }`}
            >
              {processing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <span>Place Order</span>
                  <ArrowRight size={17} />
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      {/* MARK: - Address selection sheet (mirrors iOS address sheet) */}
      <Sheet
        open={showingAddressSheet}
        onClose={() => setShowingAddressSheet(false)}
        detent="large"
        title="Select Delivery Address"
      >
        <div className="max-w-lg mx-auto">
          <ListSection>
            {addresses.length === 0 ? (
              <div className="px-4 py-6 text-center">
                <p className="text-[15px] text-[#6D6D72]">No saved addresses found</p>
              </div>
            ) : (
              addresses.map((address) => (
                <button
                  key={address.id}
                  type="button"
                  onClick={() => handleAddressSelect(address)}
                  className="w-full text-left px-4 py-3 active:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-[16px] font-semibold text-gray-900 flex-1 truncate">{address.name}</span>
                    {islandOf(address.island, address.coordinates) && (
                      <span className={`text-[11px] shrink-0 ${
                        restaurantIslandKey && islandOf(address.island, address.coordinates) !== restaurantIslandKey
                          ? 'text-[#FF3B30] font-semibold'
                          : 'text-[#8E8E93]'
                      }`}>
                        {islandLabel(islandOf(address.island, address.coordinates))}
                      </span>
                    )}
                    {address.isDefault && (
                      <span className="text-[11px] text-[#007AFF] shrink-0">Default Address</span>
                    )}
                  </div>
                  <p className="text-[15px] text-[#6D6D72] mt-0.5">{formatAddress(address)}</p>
                  {address.instructions && (
                    <p className="text-[13px] text-[#8E8E93] mt-0.5">{address.instructions}</p>
                  )}
                </button>
              ))
            )}
            {/* Add Address row */}
            <button
              type="button"
              onClick={() => {
                setShowingAddressSheet(false);
                // iOS returns to checkout after saving — carry the origin
                // so AddressFormView can route back + auto-select it.
                navigate('/add-address', { state: { from: 'checkout' } });
              }}
              className="w-full flex items-center gap-2 px-4 py-3 text-[#007AFF] active:bg-gray-100 transition-colors"
            >
              <Plus size={17} />
              <span className="text-[16px]">Add Address</span>
            </button>
          </ListSection>
        </div>
      </Sheet>

      {/* MARK: - Order Placed alert (mirrors iOS .alert "Order Placed!") */}
      {placedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div
            className="bg-white rounded-2xl max-w-xs w-full p-5 shadow-2xl text-center"
            role="alertdialog"
            aria-label="Order Placed!"
          >
            <h2 className="text-[17px] font-semibold text-gray-900">Order Placed!</h2>
            <div className="mt-2 space-y-1.5">
              <p className="text-[13px] text-[#6D6D72]">
                Your order has been placed successfully!
                {placedOrder.authorizationId && (
                  <>
                    <br />
                    Transaction ID: {placedOrder.authorizationId}
                  </>
                )}
              </p>
              {placedOrder.tip > 0 && (
                <p className="text-[13px] text-[#34C759]">
                  💚 Thank you for tipping your driver {formatCurrencyXCD(placedOrder.tip)}!
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={handleOrderConfirmed}
              className="mt-4 w-full py-2.5 bg-[#F2F2F7] text-[#FF3B30] text-[17px] font-semibold rounded-xl active:bg-gray-200"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Main Checkout Page Component
function CheckoutPage() {
  const { items } = useCart();
  const navigate = useNavigate();

  // Redirect if cart is empty
  useEffect(() => {
    if (items.length === 0) {
      navigate('/cart');
    }
  }, [items.length, navigate]);

  // Redirect if no items
  if (items.length === 0) {
    return null;
  }

  return <CheckoutForm />;
}

export default CheckoutPage;
